import { forwardRef, useCallback, useImperativeHandle, useRef, useState, useEffect, type KeyboardEvent, type ChangeEvent, type DragEvent } from 'react';
import { DOMAIN_OPTIONS } from '../types';
import { uploadImage, setPendingImageKeys } from '../api';
import { RESEARCH_BRIEF_UI_ENABLED, JURISDICTIONS_UI_ENABLED, IMAGE_ATTACH_UI_ENABLED } from '../constants';
import { resizeImage } from '../utils/imageResize';

// ─── Image Attachment Types & Constants ──────────────────────────

interface AttachedImage {
  id: string;
  file: File;
  preview: string;       // Object URL for thumbnail
  blob?: Blob;           // Resized blob for upload
  media_type?: string;
  sizeBytes: number;
  status: 'processing' | 'ready' | 'uploading' | 'error';
  error?: string;
}

const MAX_IMAGES = 4;
const MAX_RAW_SIZE = 10 * 1024 * 1024; // 10MB pre-resize

// ─── D2: Complex / multi-part query detection ────────────────────
// Suggests (never auto-switches) Research Brief mode. Blends a length
// signal with structural multi-part signals so short-but-complex queries
// are caught and long-but-simple ones aren't over-triggered.
function detectComplexQuery(raw: string): boolean {
  const q = raw.trim();
  if (q.length < 40) return false;            // too short to be a real brief
  if (q.length > 500) return true;            // long queries: keep prior behaviour

  let score = 0;

  // Length >=150 chars is a moderate signal on its own
  if (q.length >= 150) score += 1;

  // Multiple distinct questions
  const questionMarks = (q.match(/\?/g) || []).length;
  if (questionMarks >= 2) score += 2;

  // Explicit enumeration: "1.", "2)", bullet points, "a)"
  if (/(^|\n)\s*(\d+[.)]|[-*•]|[a-d][.)])\s+/m.test(q)) score += 2;

  // Coordinating conjunctions joining distinct asks
  if (/\b(and also|as well as|along with|in addition|additionally|furthermore|moreover)\b/i.test(q)) score += 2;
  // "both X and Y" pattern
  if (/\bboth\b.{1,60}\band\b/i.test(q)) score += 2;
  // "X and Y and Z" — three-plus coordinated items
  if ((q.match(/\b and \b/gi) || []).length >= 2) score += 1;

  // Comparison / differential language
  if (/\b(compare|comparison|difference(s)? between|versus|vs\.?|how does .* differ)\b/i.test(q)) score += 2;

  // Multiple distinct standard references (e.g. AS 3000, NCC, AS/NZS 3500)
  const stdRefs = q.match(/\b(AS\/NZS|AS|NCC|BCA|ISO|IEC|EN)\s?\d{3,4}/gi) || [];
  const uniqueStdRefs = new Set(stdRefs.map(s => s.toUpperCase().replace(/\s+/g, '')));
  if (uniqueStdRefs.size >= 2) score += 2;

  // Multi-domain language
  if (/\b(electrical|plumbing|structural|fire|glazing|waterproofing|acoustic|thermal)\b.*\b(electrical|plumbing|structural|fire|glazing|waterproofing|acoustic|thermal)\b/i.test(q)) score += 2;

  // Request for thoroughness / depth
  if (/\b(comprehensive|in depth|in-depth|detailed analysis|full (analysis|breakdown|review)|full assessment|everything (i|we) need|all (the )?requirements|review all|assess (all|compliance|requirements)|assessment of)\b/i.test(q)) score += 1;

  return score >= 3;
}

// ─── D2: localStorage suppression after repeated dismissals ──────────────────
const LS_BRIEF_DISMISSAL_KEY = 'ch-brief-suggestion-dismissals';
const BRIEF_DISMISSAL_THRESHOLD = 5;

function getBriefDismissalCount(): number {
  try { return parseInt(localStorage.getItem(LS_BRIEF_DISMISSAL_KEY) ?? '0', 10); } catch { return 0; }
}
function incrementBriefDismissalCount(): void {
  try { localStorage.setItem(LS_BRIEF_DISMISSAL_KEY, String(getBriefDismissalCount() + 1)); } catch { /* ignore */ }
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ACCEPT_STRING = ACCEPTED_TYPES.join(',');

export interface QueryInputHandle {
  triggerImageAttach: () => void;
  getAttachedCount: () => number;
}

interface Props {
  onSend: (query: string) => void;
  onStop?: () => void;
  disabled: boolean;
  isStreaming?: boolean;
  /** Phase 8: currently active domain filter slugs */
  activeDomains?: string[];
  /** Phase 8: callback to update active domain filters */
  onDomainsChange?: (domains: string[]) => void;
  /** Phase 8: plain english mode active */
  plainEnglish?: boolean;
  /** Checklist mode active */
  checklistMode?: boolean;
  /** Callback to toggle checklist mode */
  onChecklistModeChange?: (enabled: boolean) => void;
  /** Research Brief mode active */
  researchBriefMode?: boolean;
  /** Callback to toggle research brief mode */
  onResearchBriefModeChange?: (enabled: boolean) => void;
  /** D2: Submit query directly as a research brief (bypasses researchBriefMode state lag) */
  onSendAsResearchBrief?: (query: string) => void;
  /** SBS comparison mode active */
  sbsMode?: boolean;
  /** Callback to toggle SBS comparison mode */
  onSbsModeChange?: (enabled: boolean) => void;
  /** New Chat handler */
  onNewChat?: () => void;
  /** Save current query as a named template */
  onSaveTemplate?: (query: string) => void;
}

const QueryInput = forwardRef<QueryInputHandle, Props>(function QueryInput({ onSend, onStop, disabled, isStreaming = false, activeDomains = [], onDomainsChange, plainEnglish = false, checklistMode = false, onChecklistModeChange, researchBriefMode = false, onResearchBriefModeChange, onSendAsResearchBrief, sbsMode = false, onSbsModeChange, onNewChat, onSaveTemplate }: Props, ref) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [charCount, setCharCount] = useState(0);
  const [showDomainPicker, setShowDomainPicker] = useState(false);
  const [showBriefSuggestion, setShowBriefSuggestion] = useState(false);
  const briefSuggestionDismissedRef = useRef(false);
  const [showChecklistSuggestion, setShowChecklistSuggestion] = useState(false);
  const checklistSuggestionDismissedRef = useRef(false);

  // ─── Image attachment state ───
  const [attachedImages, setAttachedImages] = useState<AttachedImage[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const dragCounterRef = useRef(0);

  // Expose imperative handle for toolbar to trigger image attach
  useImperativeHandle(ref, () => ({
    triggerImageAttach: () => { if (IMAGE_ATTACH_UI_ENABLED) fileInputRef.current?.click(); },
    getAttachedCount: () => attachedImages.length,
  }), [attachedImages.length]);

  // F50: a session restored with sbsMode=true could otherwise keep submitting
  // comparisons invisibly while the pill is paused — force it off.
  useEffect(() => {
    if (!JURISDICTIONS_UI_ENABLED && sbsMode) onSbsModeChange?.(false);
  }, [sbsMode, onSbsModeChange]);

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      attachedImages.forEach(img => URL.revokeObjectURL(img.preview));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Process selected files: validate, resize, create previews
  const processFiles = useCallback(async (files: File[]) => {
    // F50: photo analysis paused pending verification — block every ingress
    // (picker, paste, drag-drop) at the single choke point, loudly.
    if (!IMAGE_ATTACH_UI_ENABLED) {
      setUploadError('Photo analysis is paused pending verification — it returns once its verification batch passes.');
      return;
    }
    setUploadError(null);
    const remaining = MAX_IMAGES - attachedImages.length;
    if (remaining <= 0) {
      setUploadError(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }

    const toProcess = files.slice(0, remaining);
    const newImages: AttachedImage[] = [];

    for (const file of toProcess) {
      const id = crypto.randomUUID();
      const preview = URL.createObjectURL(file);

      // Validate type
      if (!ACCEPTED_TYPES.includes(file.type)) {
        newImages.push({
          id, file, preview, sizeBytes: file.size,
          status: 'error', error: `Unsupported: ${file.type.split('/')[1] || file.type}`,
        });
        continue;
      }

      // Validate size
      if (file.size > MAX_RAW_SIZE) {
        newImages.push({
          id, file, preview, sizeBytes: file.size,
          status: 'error', error: `Too large (${(file.size / 1024 / 1024).toFixed(1)}MB)`,
        });
        continue;
      }

      newImages.push({
        id, file, preview, sizeBytes: file.size, status: 'processing',
      });
    }

    setAttachedImages(prev => [...prev, ...newImages]);

    // Resize in background
    for (const img of newImages) {
      if (img.status !== 'processing') continue;
      try {
        const resized = await resizeImage(img.file);
        setAttachedImages(prev => prev.map(i =>
          i.id === img.id
            ? { ...i, blob: resized.blob, media_type: resized.media_type, sizeBytes: resized.sizeBytes, status: 'ready' as const }
            : i
        ));
      } catch {
        setAttachedImages(prev => prev.map(i =>
          i.id === img.id ? { ...i, status: 'error' as const, error: 'Resize failed' } : i
        ));
      }
    }
  }, [attachedImages.length]);

  const removeImage = useCallback((id: string) => {
    setAttachedImages(prev => {
      const removed = prev.find(i => i.id === id);
      if (removed) URL.revokeObjectURL(removed.preview);
      return prev.filter(i => i.id !== id);
    });
    setUploadError(null);
  }, []);

  // ─── Drag & drop handlers ───
  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer?.types.includes('Files')) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;
    const files = Array.from(e.dataTransfer?.files || [])
      .filter(f => ACCEPTED_TYPES.includes(f.type));
    if (files.length > 0) {
      void processFiles(files);
    }
  }, [processFiles]);

  const handleFileSelect = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      void processFiles(files);
    }
    // Reset input so the same file can be re-selected
    e.target.value = '';
  }, [processFiles]);

  /* Auto-resize the textarea to fit content (up to CSS max-height) */
  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      autoResize();
      const val = e.target.value;
      const len = val.length;
      setCharCount(len);
      // D2: Auto-suggest Research Brief for complex/multi-part queries.
      // Detection blends length with genuine multi-part signals so short-but-complex
      // queries are caught and long-but-simple ones are not over-triggered.
      const isComplexQuery = detectComplexQuery(val);
      // Suppress permanently after threshold dismissals (user clearly prefers normal mode)
      const briefSuppressed = getBriefDismissalCount() >= BRIEF_DISMISSAL_THRESHOLD;
      // F46: Research Brief is not part of the product — never suggest it
      if (RESEARCH_BRIEF_UI_ENABLED && isComplexQuery && !researchBriefMode && !briefSuggestionDismissedRef.current && !briefSuppressed) {
        setShowBriefSuggestion(true);
      } else if (!isComplexQuery) {
        setShowBriefSuggestion(false);
        briefSuggestionDismissedRef.current = false;
      }
      // Auto-suggest checklist mode for checklist-like queries
      const checklistPattern = /\b(checklist|inspection|requirements? for|what do i need|audit|compliance list)\b/i;
      if (checklistPattern.test(val) && !checklistMode && !checklistSuggestionDismissedRef.current && val.length > 10) {
        setShowChecklistSuggestion(true);
      } else if (!checklistPattern.test(val)) {
        setShowChecklistSuggestion(false);
        checklistSuggestionDismissedRef.current = false;
      }
    },
    [autoResize, researchBriefMode],
  );

  const submit = useCallback(async () => {
    const el = textareaRef.current;
    if (!el) return;
    const value = el.value.trim();
    const hasImgs = attachedImages.some(i => i.status === 'ready');

    // Require either text or images
    if (!value && !hasImgs) return;
    if (disabled || isUploading) return;

    // Upload images to R2 first, then set pending keys for streamQuery
    if (hasImgs) {
      const readyImages = attachedImages.filter(i => i.status === 'ready' && i.blob);
      try {
        setIsUploading(true);
        setUploadError(null);
        const keys = await Promise.all(
          readyImages.map(img => uploadImage(img.blob!, img.file.name))
        );
        setPendingImageKeys(keys);
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : 'Failed to upload images');
        setIsUploading(false);
        return;
      }
      setIsUploading(false);
    }

    // Auto-generate query text when only images (no text)
    const queryText = value || 'Analyse this image for compliance marks, labels, and standards references.';
    onSend(queryText);
    el.value = '';
    el.style.height = 'auto';
    setCharCount(0);
    // Clear attached images
    attachedImages.forEach(img => URL.revokeObjectURL(img.preview));
    setAttachedImages([]);
    setUploadError(null);
  }, [disabled, onSend, attachedImages, isUploading]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void submit();
      }
    },
    [submit],
  );

  // Paste handler for clipboard images (Ctrl+V / Cmd+V screenshots)
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file' && ACCEPTED_TYPES.includes(item.type)) {
        const file = item.getAsFile();
        if (file) imageFiles.push(file);
      }
    }
    if (imageFiles.length > 0) {
      e.preventDefault();
      void processFiles(imageFiles);
    }
  }, [processFiles]);

  const handleStop = useCallback(() => {
    onStop?.();
  }, [onStop]);

  const removeDomain = useCallback((slug: string) => {
    onDomainsChange?.(activeDomains.filter(d => d !== slug));
  }, [activeDomains, onDomainsChange]);

  const addDomain = useCallback((slug: string) => {
    if (!activeDomains.includes(slug)) {
      onDomainsChange?.([...activeDomains, slug]);
    }
    setShowDomainPicker(false);
  }, [activeDomains, onDomainsChange]);

  const domainLabel = (slug: string) =>
    DOMAIN_OPTIONS.find(d => d.slug === slug)?.label ?? slug;

  // Derived: which mode pill is active (mutually exclusive)
  const activeMode = checklistMode ? 'checklist' : researchBriefMode ? 'brief' : sbsMode ? 'jurisdictions' : 'normal';

  // Fix #3: Close domain picker on ESC key or click outside
  const domainPickerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showDomainPicker) return;
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowDomainPicker(false);
      }
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (domainPickerRef.current && !domainPickerRef.current.contains(e.target as Node)) {
        setShowDomainPicker(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDomainPicker]);

  return (
    <div
      className={`query-input-wrap${isDragging ? ' query-input-wrap--dragging' : ''}`}
      style={{ flexDirection: 'column', alignItems: 'stretch', position: 'relative' }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Hidden file input for image selection */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_STRING}
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        aria-hidden="true"
      />

      {/* Drag-drop overlay */}
      {isDragging && (
        <div className="image-drop-overlay" aria-hidden="true">
          <div className="image-drop-overlay-content">
            <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            <span>Drop compliance photos here</span>
            <span className="image-drop-hint">JPEG, PNG, WebP · Max {MAX_IMAGES} images</span>
          </div>
        </div>
      )}
      {/* Active domain/plain-english chips — mode chips removed (now shown via pill bar) */}
      {(plainEnglish || activeDomains.length > 0) && (
        <div className="domain-chip-bar" data-tour="domain-chips" title="Active filters — click ✕ to remove. Add filters via Settings > Domain Filters.">
          {plainEnglish && (
            <span className="domain-chip domain-chip--plain">📖 Plain English</span>
          )}
          {activeDomains.map(slug => (
            <span key={slug} className="domain-chip">
              {domainLabel(slug)}
              <button className="domain-chip-remove" onClick={() => removeDomain(slug)} aria-label={`Remove ${domainLabel(slug)} filter`} type="button">✕</button>
            </span>
          ))}
        </div>
      )}
      {/* Image thumbnail previews */}
      {attachedImages.length > 0 && (
        <div className="image-thumbnails">
          {attachedImages.map(img => (
            <div
              key={img.id}
              className={`image-thumb${img.status === 'error' ? ' image-thumb--error' : ''}${img.status === 'processing' ? ' image-thumb--processing' : ''}`}
            >
              <img src={img.preview} alt="Attached" className="image-thumb-img" />
              {img.status === 'processing' && (
                <div className="image-thumb-spinner" aria-label="Processing">⏳</div>
              )}
              {img.status === 'error' && (
                <div className="image-thumb-error-badge" title={img.error}>⚠️</div>
              )}
              <button
                className="image-thumb-remove"
                onClick={() => removeImage(img.id)}
                aria-label="Remove image"
                type="button"
              >
                ×
              </button>
            </div>
          ))}
          {attachedImages.length < MAX_IMAGES && (
            <button
              className="image-thumb-add"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Add another image"
              type="button"
              title="Add another image"
            >
              +
            </button>
          )}
        </div>
      )}

      {/* Upload error */}
      {uploadError && (
        <div className="image-upload-error" role="alert">
          ⚠️ {uploadError}
        </div>
      )}

      {/* Uploading indicator */}
      {isUploading && (
        <div className="image-uploading" role="status">
          ⏳ Uploading images...
        </div>
      )}

      {/* Mode pill bar — mutually exclusive query mode selector */}
      <div className="mode-pill-bar" role="group" aria-label="Query mode" data-tour="mode-pills">
        {(['normal', 'checklist', 'jurisdictions'] as const).map((mode) => {
          const labels: Record<string, string> = {
            normal: 'Normal',
            checklist: '✓ Checklist',
            jurisdictions: '⚖ Jurisdictions',
          };
          // F50: paused surfaces stay visible as placeholders, never clickable
          const paused = mode === 'jurisdictions' && !JURISDICTIONS_UI_ENABLED;
          const isActive = !paused && activeMode === mode;
          return (
            <button
              key={mode}
              type="button"
              className={`mode-pill${isActive ? ' mode-pill--active' : ''}`}
              style={paused ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
              disabled={paused}
              title={paused ? 'Paused — returns after its verification batch passes' : undefined}
              onClick={() => {
                if (paused) return;
                // Clicking active pill → back to Normal; clicking another → activate it
                const next = isActive && mode !== 'normal' ? 'normal' : mode;
                onChecklistModeChange?.(next === 'checklist');
                onResearchBriefModeChange?.(false); // Brief removed from mode bar
                onSbsModeChange?.(next === 'jurisdictions');
              }}
              aria-pressed={isActive}
            >
              {labels[mode]}{paused ? ' · SOON' : ''}
            </button>
          );
        })}
      </div>

      <div data-tour="query-input" className="query-input-row">
      {/* +Filter button inline with textarea */}
      <div ref={domainPickerRef} style={{ position: 'relative', flexShrink: 0 }}>
        <button
          className="filter-inline-btn"
          onClick={() => setShowDomainPicker(v => !v)}
          aria-label="Add domain filter"
          type="button"
          title="Filter by domain"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          {activeDomains.length > 0 ? `Filter (${activeDomains.length})` : '+ Filter'}
        </button>
        {showDomainPicker && (
          <div className="domain-picker domain-picker--from-inline">
            {DOMAIN_OPTIONS.filter(d => !activeDomains.includes(d.slug)).map(({ slug, label }) => (
              <button
                key={slug}
                className="domain-picker-item"
                onClick={() => addDomain(slug)}
                type="button"
              >
                {label}
              </button>
            ))}
            {activeDomains.length > 0 && (
              <button
                className="domain-picker-item domain-picker-item--clear"
                onClick={() => { onDomainsChange?.([]); setShowDomainPicker(false); }}
                type="button"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>
      <textarea
        ref={textareaRef}
        className="query-textarea"
        rows={1}
        placeholder={isStreaming ? 'Responding...' : researchBriefMode ? 'Ask a complex compliance question... (up to 20,000 characters)' : 'Ask about any standard, clause, or requirement...'}
        disabled={disabled || isUploading}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        aria-label="Query input"
        maxLength={researchBriefMode ? 20000 : 2000}
      />
      {/* Inline attach icon button — F50: photo analysis paused pending verification */}
      <button
        className="attach-inline-btn"
        onClick={() => { if (IMAGE_ATTACH_UI_ENABLED) fileInputRef.current?.click(); }}
        aria-label={IMAGE_ATTACH_UI_ENABLED ? 'Attach image' : 'Photo analysis paused pending verification'}
        type="button"
        style={!IMAGE_ATTACH_UI_ENABLED ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
        title={IMAGE_ATTACH_UI_ENABLED ? 'Attach compliance photo' : 'Photo analysis paused — returns after its verification batch passes'}
        disabled={!IMAGE_ATTACH_UI_ENABLED || disabled || attachedImages.length >= MAX_IMAGES}
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
        </svg>
      </button>
      {/* Save as template button — only shown when there's text in the input */}
      {onSaveTemplate && !isStreaming && (
        <button
          className="save-template-btn"
          onClick={() => {
            const val = textareaRef.current?.value.trim();
            if (val) onSaveTemplate(val);
          }}
          aria-label="Save as template"
          title="Save as template"
          type="button"
          disabled={disabled}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
            <polyline points="17 21 17 13 7 13 7 21" />
            <polyline points="7 3 7 8 15 8" />
          </svg>
        </button>
      )}
      {isStreaming ? (
        <button
          className="stop-btn"
          onClick={handleStop}
          aria-label="Stop generating"
          title="Stop generating"
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="6" width="12" height="12" rx="2" />
          </svg>
        </button>
      ) : (
        <button
          className="send-btn"
          onClick={() => void submit()}
          disabled={disabled || isUploading}
          aria-label="Send query"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      )}

      {/* Char counter — inside the query row, bottom-right of textarea */}
      <div
        className="char-limit-inline"
        aria-live="polite"
      >
        {charCount > 0
          ? `${charCount}/${researchBriefMode ? '20,000' : '2,000'}`
          : researchBriefMode
            ? 'Up to 20,000 characters'
            : 'Up to 2,000 characters'
        }
      </div>
      </div>
      {/* Research Brief auto-suggestion banner */}
      {showBriefSuggestion && !researchBriefMode && (
        <div className="research-brief-suggestion">
          {/* D2: × dismiss — don't re-suggest for this query; increments towards threshold */}
          <button
            className="research-brief-suggestion-dismiss"
            onClick={() => {
              setShowBriefSuggestion(false);
              briefSuggestionDismissedRef.current = true;
              incrementBriefDismissalCount();
            }}
            type="button"
            aria-label="Dismiss suggestion"
          >
            ×
          </button>
          <span>
            💡 This looks like a complex query.{' '}
            <strong>Research Brief</strong> breaks it into structured sections,
            each independently searched and cited — better for multi-topic questions.
          </span>
          <div className="research-brief-suggestion-actions">
            <button
              className="research-brief-suggestion-primary"
              onClick={() => {
                const value = textareaRef.current?.value.trim();
                if (!value) return;
                setShowBriefSuggestion(false);
                briefSuggestionDismissedRef.current = true;
                // Activate brief mode chip so UI reflects active mode
                onResearchBriefModeChange?.(true);
                // D2: auto-submit directly as research brief
                // (bypasses researchBriefMode state which hasn't flushed yet)
                if (onSendAsResearchBrief) {
                  onSendAsResearchBrief(value);
                  // Clear the textarea
                  if (textareaRef.current) {
                    textareaRef.current.value = '';
                    textareaRef.current.style.height = 'auto';
                  }
                  setCharCount(0);
                }
              }}
              type="button"
            >
              Try Research Brief →
            </button>
          </div>
        </div>
      )}
      {/* Checklist auto-suggestion banner (fix #7) */}
      {showChecklistSuggestion && !checklistMode && (
        <div className="checklist-suggestion">
          <span>📋 Generate as checklist?</span>
          <div className="checklist-suggestion-actions">
            <button
              className="checklist-suggestion-primary"
              onClick={() => {
                onChecklistModeChange?.(true);
                setShowChecklistSuggestion(false);
              }}
              type="button"
            >
              Generate Checklist ☑️
            </button>
            <button
              className="checklist-suggestion-secondary"
              onClick={() => {
                setShowChecklistSuggestion(false);
                checklistSuggestionDismissedRef.current = true;
              }}
              type="button"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

    </div>
  );
});

export default QueryInput;
